import React from 'react';

import { ReportContext } from './hooks/use-context';
import type { EventReportData, QuestionData } from './types';

// Helper function to format date from API response
function formatDate(dateString: string): string {
  if (!dateString) return '';
  const [datePart] = dateString.split(' ');
  return datePart || '';
}

// Helper function to format time from API response
function formatTime(dateString: string): string {
  if (!dateString) return '';
  const parts = dateString.split(' ');
  if (parts.length < 2) return '';
  const [hours, minutes] = parts[1].split(':');
  return `${hours}:${minutes}`;
}

// Helper to extract month name (pt-BR) from date string like 'DD/MM/YYYY HH:mm'
function getMonthNameFromDateString(dateString: string): string {
  if (!dateString) return '';
  const [datePart] = dateString.split(' ');
  const parts = (datePart || '').split('/');
  if (parts.length < 2) return '';
  const monthNum = Number(parts[1]);
  const MONTHS = [
    'janeiro',
    'fevereiro',
    'março',
    'abril',
    'maio',
    'junho',
    'julho',
    'agosto',
    'setembro',
    'outubro',
    'novembro',
    'dezembro',
  ];
  if (!Number.isFinite(monthNum) || monthNum < 1 || monthNum > 12) return '';
  return MONTHS[monthNum - 1];
}

// Helper function to get answer content by question NAME
function getAnswerContentByName(
  questions: QuestionData[],
  questionName: string,
): string {
  const question = questions.find((q) => q.name === questionName);
  if (!question || !question.answers || question.answers.length === 0)
    return '';

  const answer = question.answers[0];
  return answer.option || answer.content || '';
}

export function IzuReportReceipt(props: { reportData: EventReportData }) {
  const { reportData } = props;

  // Extract questions from the first section
  const questions =
    reportData?.body?.items?.[0]?.sections?.[0]?.questions || [];
  const header = reportData?.header;
  const firstItem = reportData?.body?.items?.[0];

  // Question NAMES based on API example for Recebimento
  const QUESTION_NAMES = {
    produto: 'Produto',
    fornecedor: 'Fornecedor',
    temperatura: 'Temperatura do Produto (ºC)',
    validade: 'Validade',
    condicoesEntregador: 'Condições do Entregador',
    condicoesEmbalagem: 'Condições da Embalagem',
    acoesCorretivas: 'Ações Corretivas',
    responsavel: 'Colaborador Responsável',
    assinatura: 'Assinatura do Colaborador Responsável',
  } as const;

  const eventDateTime =
    header?.event_finished_at ||
    header?.event_date_time ||
    header?.event_created_at ||
    '';
  const date = formatDate(eventDateTime);
  // const time = formatTime(eventDateTime);
  const monthName = getMonthNameFromDateString(eventDateTime);
  const produto = getAnswerContentByName(questions, QUESTION_NAMES.produto);
  const fornecedor = getAnswerContentByName(
    questions,
    QUESTION_NAMES.fornecedor,
  );
  const temperatura = getAnswerContentByName(
    questions,
    QUESTION_NAMES.temperatura,
  );
  const validade = getAnswerContentByName(questions, QUESTION_NAMES.validade);
  const condicoesEntregador = getAnswerContentByName(
    questions,
    QUESTION_NAMES.condicoesEntregador,
  );
  const condicoesEmbalagem = getAnswerContentByName(
    questions,
    QUESTION_NAMES.condicoesEmbalagem,
  );
  const acoesCorretivas = getAnswerContentByName(
    questions,
    QUESTION_NAMES.acoesCorretivas,
  );
  const responsavel = getAnswerContentByName(
    questions,
    QUESTION_NAMES.responsavel,
  );
  // const assinatura = getAnswerContentByName(questions, QUESTION_NAMES.assinatura);

  // Footer data from API
  const elaboradoPor = firstItem?.formulary_last_author || '-';
  const dataElaboracao = firstItem?.formulary_updated_at || '-';
  const aprovadoPor = header?.event_validated_author || '-';
  const dataRevisao = header?.event_validated_at || '-';

  return (
    <ReportContext value={reportData}>
      <div
        style={{
          width: '210mm',
          minHeight: '297mm',
          backgroundColor: '#ffffff',
          fontFamily: 'Arial, sans-serif',
          padding: '20mm',
          margin: '0 auto',
          boxSizing: 'border-box',
        }}
      >
        {/* Header Section */}
        <div style={{ marginBottom: '10px' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              border: '2px solid #000',
            }}
          >
            <tbody>
              <tr>
                <td
                  style={{
                    width: '20%',
                    padding: '10px',
                    border: '1px solid #000',
                    textAlign: 'center',
                  }}
                >
                  {header?.logo && (
                    <img
                      src={header.logo}
                      alt="Logo"
                      style={{ maxWidth: '100px', height: 'auto' }}
                    />
                  )}
                </td>
                <td
                  style={{
                    width: '50%',
                    padding: '10px',
                    border: '1px solid #000',
                    textAlign: 'center',
                    fontWeight: 'bold',
                    fontSize: '16px',
                  }}
                >
                  CONTROLE DE RECEBIMENTO DE PRODUTOS ALIMENTÍCIOS
                </td>
                <td
                  style={{
                    width: '30%',
                    padding: '10px',
                    border: '1px solid #000',
                  }}
                >
                  <div style={{ marginBottom: '5px' }}>PCQ1-07</div>
                  <div>Página: 1 de 1</div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Month and Area Section */}
        <div style={{ marginBottom: '8px', fontSize: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>Mês: {monthName}</div>
            {header?.local?.name && <div>Estoque de {header?.local?.name}</div>}
          </div>
        </div>

        {/* Main Data Table */}
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            border: '2px solid #000',
            marginBottom: '12px',
            fontSize: '12px',
          }}
        >
          <thead>
            <tr style={{ backgroundColor: '#808080', color: '#fff' }}>
              <th style={{ border: '1px solid #000', padding: '6px' }}>Data</th>
              <th style={{ border: '1px solid #000', padding: '6px' }}>
                Produto
              </th>
              <th style={{ border: '1px solid #000', padding: '6px' }}>
                Fornecedor
              </th>
              <th style={{ border: '1px solid #000', padding: '6px' }}>
                Temp. °C
              </th>
              <th style={{ border: '1px solid #000', padding: '6px' }}>
                Validade
              </th>
              <th style={{ border: '1px solid #000', padding: '6px' }}>
                Condições do entregador
              </th>
              <th style={{ border: '1px solid #000', padding: '6px' }}>
                Condições da embalagem
              </th>
              <th style={{ border: '1px solid #000', padding: '6px' }}>
                Ação corretiva
              </th>
              <th style={{ border: '1px solid #000', padding: '6px' }}>
                Responsável
              </th>
            </tr>
          </thead>
          <tbody>
            {/* First row with actual data */}
            <tr>
              <td
                style={{
                  border: '1px solid #000',
                  padding: '6px',
                  textAlign: 'center',
                }}
              >
                {date}
              </td>
              <td style={{ border: '1px solid #000', padding: '6px' }}>
                {produto}
              </td>
              <td style={{ border: '1px solid #000', padding: '6px' }}>
                {fornecedor}
              </td>
              <td
                style={{
                  border: '1px solid #000',
                  padding: '6px',
                  textAlign: 'center',
                }}
              >
                {temperatura}
              </td>
              <td
                style={{
                  border: '1px solid #000',
                  padding: '6px',
                  textAlign: 'center',
                }}
              >
                {validade}
              </td>
              <td style={{ border: '1px solid #000', padding: '6px' }}>
                {condicoesEntregador}
              </td>
              <td style={{ border: '1px solid #000', padding: '6px' }}>
                {condicoesEmbalagem}
              </td>
              <td style={{ border: '1px solid #000', padding: '6px' }}>
                {acoesCorretivas}
              </td>
              <td style={{ border: '1px solid #000', padding: '6px' }}>
                {responsavel}
              </td>
            </tr>
            {/* Empty rows for additional entries */}
            {Array.from({ length: 8 }).map((_, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={`empty_${index}`}>
                <td style={{ border: '1px solid #000', padding: '14px' }}>
                  &nbsp;
                </td>
                <td style={{ border: '1px solid #000', padding: '14px' }}>
                  &nbsp;
                </td>
                <td style={{ border: '1px solid #000', padding: '14px' }}>
                  &nbsp;
                </td>
                <td style={{ border: '1px solid #000', padding: '14px' }}>
                  &nbsp;
                </td>
                <td style={{ border: '1px solid #000', padding: '14px' }}>
                  &nbsp;
                </td>
                <td style={{ border: '1px solid #000', padding: '14px' }}>
                  &nbsp;
                </td>
                <td style={{ border: '1px solid #000', padding: '14px' }}>
                  &nbsp;
                </td>
                <td style={{ border: '1px solid #000', padding: '14px' }}>
                  &nbsp;
                </td>
                <td style={{ border: '1px solid #000', padding: '14px' }}>
                  &nbsp;
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Recommendations Section */}
        <div style={{ marginBottom: '12px' }}>
          <div
            style={{
              fontWeight: 'bold',
              marginBottom: '6px',
              fontSize: '12px',
            }}
          >
            Temperatura recomendada:
          </div>
          <div style={{ fontSize: '11px', lineHeight: '1.5' }}>
            <div>
              <strong>Produtos refrigerados:</strong> Até 7ºC; Frutos do mar e
              peixes frescos: até 2ºC;
            </div>
            <div>
              <strong>Produtos congelados:</strong> -18º até -12ºC, sem sinais
              de descongelamento.
            </div>
            <div>
              <strong>Condições do entregador:</strong>{' '}
              <strong>C – Conforme:</strong> Entregador limpo
              <span style={{ float: 'right' }}>
                <strong>NC – Não conforme:</strong> Uniforme sujo, má
                apresentação pessoal
              </span>
            </div>
            <div>
              <strong>Condições da embalagem:</strong>{' '}
              <strong>C – Conforme:</strong> Embalagens íntegras
              <span style={{ float: 'right' }}>
                <strong>NC – Não conforme:</strong> Embalagem rasgada, furada,
                estufada
              </span>
            </div>
          </div>
        </div>

        {/* Footer Table */}
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            border: '2px solid #000',
            fontSize: '11px',
          }}
        >
          <tbody>
            <tr>
              <td
                style={{
                  border: '1px solid #000',
                  padding: '8px',
                  width: '33%',
                }}
              >
                <strong>Elaboração</strong>
                <div>
                  <strong>Elaborado por:</strong> {elaboradoPor}
                </div>
                <div>
                  <strong>Data de elaboração:</strong> {dataElaboracao}
                </div>
              </td>
              <td
                style={{
                  border: '1px solid #000',
                  padding: '8px',
                  width: '33%',
                }}
              >
                <strong>Aprovação</strong>
                <div>
                  <strong>Aprovado por:</strong> {aprovadoPor}
                </div>
                <div>
                  <strong>Data de aprovação:</strong> {date}
                </div>
              </td>
              <td
                style={{
                  border: '1px solid #000',
                  padding: '8px',
                  width: '34%',
                }}
              >
                <strong>Revisão</strong>
                <div>
                  <strong>Código:</strong> 001
                </div>
                <div>
                  <strong>Data da revisão:</strong> {dataRevisao}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </ReportContext>
  );
}
